import type { ReactNode } from 'react';
import { AlertIcon, CheckIcon, InfoIcon } from './icons.js';

/**
 * A message about something that just happened, or about the state of the page.
 *
 * `role` is chosen by tone rather than left to the caller: a failure is an `alert`,
 * which a screen reader interrupts to read, and anything else is a polite `status`,
 * which it reads when it next pauses. Getting that backwards means either shouting
 * over someone or never telling them.
 */

export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

export interface AlertProps {
  tone?: AlertTone;
  title?: string;
  children: ReactNode;
}

const ICONS: Record<AlertTone, typeof InfoIcon> = {
  info: InfoIcon,
  success: CheckIcon,
  warning: AlertIcon,
  danger: AlertIcon,
};

export function Alert({ tone = 'info', title, children }: AlertProps): ReactNode {
  const Icon = ICONS[tone];
  return (
    <div
      className={`alert alert-${tone}`}
      role={tone === 'danger' ? 'alert' : 'status'}
      aria-live={tone === 'danger' ? 'assertive' : 'polite'}
    >
      <Icon className="alert-icon" />
      <div>
        {title !== undefined && <strong>{title}</strong>}
        {children}
      </div>
    </div>
  );
}
